import { BevelFilter, ColorReplaceFilter, DropShadowFilter, EmbossFilter, GlowFilter, MotionBlurFilter, OutlineFilter, } from "pixi-filters";
import * as PIXI from "pixi.js";
import { Renderer } from "../../renderer/Renderer";
/**
 * FilterBevel.ts
 */
export class FilterBevel {
    constructor(rotation = 45, thickness = 2, lightColor = 0xffffff, lightAlpha = 0.7, shadowColor = 0x000000, shadowAlpha = 0.7) {
        this.rotation = rotation;
        this.thickness = thickness;
        this.lightColor = lightColor;
        this.lightAlpha = lightAlpha;
        this.shadowColor = shadowColor;
        this.shadowAlpha = shadowAlpha;
    }
    equals(other) {
        return (this.rotation === other.rotation &&
            this.thickness === other.thickness &&
            this.lightColor === other.lightColor &&
            this.lightAlpha === other.lightAlpha &&
            this.shadowColor === other.shadowColor &&
            this.shadowAlpha === other.shadowAlpha);
    }
    createFilter() {
        const filter = new BevelFilter(this);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
/**
 * FilterDropShadow.ts
 */
export class FilterDropShadow {
    constructor(rotation = 45, distance = 5, color = 0x000000, alpha = 0.5, shadowOnly = false, blur = 2, quality = 3, kernels = null, pixelSize = 1, resolution) {
        this.rotation = rotation;
        this.distance = distance;
        this.color = color;
        this.alpha = alpha;
        this.shadowOnly = shadowOnly;
        this.blur = blur;
        this.quality = quality;
        this.kernels = kernels;
        this.pixelSize = pixelSize;
        this.resolution = resolution;
    }
    equals(other) {
        return (this.rotation === other.rotation &&
            this.distance === other.distance &&
            this.color === other.color &&
            this.alpha === other.alpha &&
            this.shadowOnly === other.shadowOnly &&
            this.blur === other.blur &&
            this.quality === other.quality &&
            this.kernels === other.kernels &&
            this.pixelSize === other.pixelSize &&
            this.resolution === other.resolution);
    }
    createFilter() {
        return new DropShadowFilter({
            rotation: this.rotation,
            distance: this.distance,
            color: this.color,
            alpha: this.alpha,
            shadowOnly: this.shadowOnly,
            blur: this.blur,
            quality: this.quality,
            kernels: this.kernels,
            pixelSize: this.pixelSize,
            resolution: this.resolution || Renderer.getInstance().getResolution(),
        });
    }
}
/**
 * FilterEmboss.ts
 */
export class FilterEmboss {
    constructor(strength = 5) {
        this.strength = strength;
    }
    equals(other) {
        return this.strength === other.strength;
    }
    createFilter() {
        const filter = new EmbossFilter(this.strength);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
/**
 * FilterGlow.ts
 */
export class FilterGlow {
    /**
     * @param distance - The distance of the glow. Make it 2 times more for resolution=2.
     * @param outerStrength - The strength of the glow outward from the edge of the sprite.
     * @param innerStrength - The strength of the glow inward from the edge of the sprite.
     * @param color - The color of the glow.
     * @param quality - A number between 0 and 1 that describes the quality of the glow. The higher the number the less performant.
     * @param knockout - Toggle to hide the contents and only show glow.
     */
    constructor(distance = 10, outerStrength = 4, innerStrength = 0, color = 0x000000, quality = 0.1, knockout = false) {
        this.distance = distance;
        this.outerStrength = outerStrength;
        this.innerStrength = innerStrength;
        this.color = color;
        this.quality = quality;
        this.knockout = knockout;
    }
    equals(other) {
        return (this.distance === other.distance &&
            this.outerStrength === other.outerStrength &&
            this.innerStrength === other.innerStrength &&
            this.color === other.color &&
            this.quality === other.quality &&
            this.knockout === other.knockout);
    }
    createFilter() {
        const filter = new GlowFilter(this);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
/**
 * FilterMotionBlur.ts
 */
export class FilterMotionBlur {
    constructor(velocity = [0, 0], kernelSize = 5, offset = 0) {
        this.velocity = velocity;
        this.kernelSize = kernelSize;
        this.offset = offset;
    }
    equals(other) {
        return (this.velocity === other.velocity &&
            this.kernelSize === other.kernelSize &&
            this.offset === other.offset);
    }
    createFilter() {
        const filter = new MotionBlurFilter(this.velocity, this.kernelSize, this.offset);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
/**
 * FilterOutline.ts
 */
export class FilterOutline {
    /**
     * @param thickness - Outline thickness.
     * @param color - Outline color.
     * @param quality - A number between 0 and 1 that describes the quality of the glow. The higher the number the less performant.
     */
    constructor(thickness = 1, color = 0x000000, quality = 0.1) {
        this.thickness = thickness;
        this.color = color;
        this.quality = quality;
    }
    equals(other) {
        return (this.thickness === other.thickness &&
            this.color === other.color &&
            this.quality === other.quality);
    }
    createFilter() {
        const filter = new OutlineFilter(this.thickness, this.color, this.quality);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
/**
 * FilterColorReplace.ts
 */
export class FilterColorReplace {
    /**
     * @param originalColor - Color to replace.
     * @param newColor - New color.
     * @param epsilon - Tolerance/sensitivity of the floating point comparison. lower = more exact, higher = more inclusive
     */
    constructor(originalColor = 0xff0000, newColor = 0x000000, epsilon = 0.4) {
        this.originalColor = originalColor;
        this.newColor = newColor;
        this.epsilon = epsilon;
    }
    equals(other) {
        return (this.originalColor === other.originalColor &&
            this.newColor === other.newColor &&
            this.epsilon === other.epsilon);
    }
    createFilter() {
        const filter = new ColorReplaceFilter(this.originalColor, this.newColor, this.epsilon);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
/**
 * FilterBrightness.ts
 * @param strength - Value of the brightness (0-1, where 0 is black)
 */
export class FilterBrightness {
    constructor(strength = 0.5) {
        this.strength = strength;
    }
    equals(other) {
        return this.strength === other.strength;
    }
    createFilter() {
        const filter = new PIXI.filters.ColorMatrixFilter();
        filter.brightness(this.strength, false);
        filter.resolution = Renderer.getInstance().getResolution();
        return filter;
    }
}
//# sourceMappingURL=FilterProperties.js.map